import { createAddressSchema } from "@/components/Form/AddressForm/-schema/addressSchema";
import { AddressForm } from "@/components/Form/AddressForm/AddressForm";
import { AddressFormContext } from "@/components/Form/AddressForm/AddressFormContext";
import type { SupportedCountry } from "@/constants/country";
import type { ErrorObject } from "@/utils/error-object";
import {
  CFormItemBase,
  type CFormItemProps,
  buildCFormItem,
} from "../CFormItem";

type Address = {
  countryCode?: SupportedCountry;
  postalCode: string;
  stateProvinceCode: string;
  city: string;
  streetAddress: string;
  addressLine2?: string;
  formattedAddress?: string;
};

type Props = Omit<CFormItemProps, "errors"> & {
  value: Address;
  onChange: (value: Address) => void;
  errorObject?: ErrorObject<Address>;
  inputClassName?: string;
};

/**
 * Card-based Form / AddressItem
 */
export const CFormAddressItem = buildCFormItem((props: Props) => {
  const formattedAddress =
    props.value?.formattedAddress ??
    [
      props.value?.streetAddress,
      props.value?.addressLine2,
      props.value?.city,
      props.value?.stateProvinceCode,
      props.value?.postalCode,
    ]
      .filter((e) => !!e)
      .join(",");

  const errors = Object.fromEntries(
    Object.entries(props.errorObject ?? {}).map(([key, value]) => [
      key,
      Array.isArray(value) ? value.join(", ") : value,
    ]),
  );

  return (
    <CFormItemBase
      {...props}
      // errors={errorMessages} エラーは AddressForm で表示する
      readComponent={formattedAddress}
      writeComponent={
        <AddressFormContext.Provider
          value={{
            values: {
              countryCode: props.value?.countryCode,
              postalCode: props.value?.postalCode,
              stateProvinceCode: props.value?.stateProvinceCode,
              city: props.value?.city,
              streetAddress: props.value?.streetAddress,
              addressLine2: props.value?.addressLine2,
            },
            errors: errors,
            touched: {
              countryCode: true,
              postalCode: true,
              stateProvinceCode: true,
              city: true,
              streetAddress: true,
              addressLine2: true,
            },
            onChange: (changes) => {
              const address = {
                ...props.value,
                ...changes,
              };
              props.onChange(address);
            },
            onBlur: (_key) => {},
            validate: (address) => {
              const schema = createAddressSchema({
                isCountryRequired: true,
                isPostalCodeRequired: true,
                isStateProvinceRequired: true,
                isCityRequired: true,
                isStreetAddressRequired: true,
              });
              const result = schema.safeParse(address);
              return result.success;
            },
          }}
        >
          <AddressForm
            isCountryRequired
            isPostalCodeRequired
            isStateProvinceRequired
            isCityRequired
            isStreetAddressRequired
          />
        </AddressFormContext.Provider>
      }
    />
  );
});
